"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Cpu, ShieldCheck, Wind, Zap, ArrowRight, SlidersHorizontal as Sliders, Scale } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const specDrivers = [{
  title: "Drive System & Motion Base",
  summary: "Powered motion mechanism and structural frame — compare across listings.",
  icon: Cpu
}, {
  title: "Operator Console",
  summary: "How the machine is controlled and stopped in an emergency.",
  icon: Sliders
}, {
  title: "Safety Arena & Blower",
  summary: "Inflatable landing area and its air supply, condition and sizing vary by listing.",
  icon: Wind
}, {
  title: "Accessories & Site Requirements",
  summary: "Cables, transport equipment, and any product-specific power or site requirements — confirm directly with the seller rather than assuming a standard spec.",
  icon: Zap
}];
export default function Pricing() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="electricbullprice" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border selection:bg-primary selection:text-primary-foreground">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        
        {/* Outer Spec Plate Container with machined corner registration markings */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative border border-border bg-card text-card-foreground p-6 sm:p-10 shadow-xs">
          {/* Corner Registration Accents */}
          <div className="absolute top-0 left-0 size-2 border-t-2 border-l-2 border-primary" aria-hidden="true" />
          <div className="absolute top-0 right-0 size-2 border-t-2 border-r-2 border-primary" aria-hidden="true" />
          <div className="absolute bottom-0 left-0 size-2 border-b-2 border-l-2 border-primary" aria-hidden="true" />
          <div className="absolute bottom-0 right-0 size-2 border-b-2 border-r-2 border-primary" aria-hidden="true" />

          {/* Header Metadata Strip */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-5 mb-8">
            <div className="flex items-center gap-2">
              <span className="size-2 bg-primary inline-block shrink-0" aria-hidden="true" />
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                Electric Bull Price Comparison
              </p>
            </div>
          </div>

          {/* Section Main Titles */}
          <div className="space-y-4 mb-8">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground leading-none">
              Electric Bull Price: What Should You Compare?
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed max-w-3xl">
              An electric bull price depends on more than the powered motion system itself. Buyers should compare the complete machine, controller, safety arena, blower, accessories and the product-specific electrical requirements. Different electric mechanical bulls may use different configurations, so one advertised price should not be treated as a fixed reference for every electric system. When comparing an electric bull for sale, look at the actual equipment included and the current listed price for that model.
            </p>
          </div>

          {/* Industrial Component Matrix Grid */}
          <div className="mt-8 border-t border-border pt-8">
            <div className="flex items-center gap-2 mb-6">
              <Scale className="size-4 text-primary shrink-0" />
              <h3 className="font-mono text-xs uppercase tracking-widest text-foreground font-semibold">
                Core Price Drivers &amp; Equipment Matrix
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {specDrivers.map((driver, index) => {
              const Icon = driver.icon;
              return <div key={driver.title} data-index={index} className="border border-border bg-background p-5 flex flex-col justify-start">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 border border-border bg-muted/40 shrink-0">
                        <Icon className="size-4 text-primary" />
                      </div>
                      <h4 className="font-serif text-lg font-bold uppercase text-foreground leading-snug">
                        {driver.title}
                      </h4>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {driver.summary}
                    </p>
                  </div>;
            })}
            </div>
          </div>

          {/* Action Footer Callout */}
          <div className="mt-8 pt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 bg-muted/20 p-4 sm:p-6 border">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" />
                <span className="font-mono text-xs uppercase font-semibold text-foreground tracking-wider">
                  Equipment Standard Verification
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Verify line-item inclusions before comparing quotes or placing orders.
              </p>
            </div>

            <Button asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs uppercase tracking-wider font-semibold h-11 px-6 transition-[background-color,border-color,box-shadow] active:scale-[0.99]">
              <Link to="#">
                <span>Compare Electric Bulls for Sale</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>

        </motion.div>

      </div>
    </section>;
}